import React, { useEffect, useRef, useState } from "react";
import { ModalWithIcon } from "../../RfModal/ModalWithIcon";
import { CancellationOfferDefinition } from "@functions/plans/info/cancellationOffers";
import { DowngradePlanTargets } from "../../../../../state/types";

type CancelPlanOffersModalProps = {
    isOpen: boolean;
    onClose: () => void;
    onCancel: () => void;
    onAcceptOffer: () => void;
    pricePerCredit: number;
    offer: CancellationOfferDefinition;
    workspaceId: string;
    currentPlan: string;
    selectedDowngradePlan: DowngradePlanTargets;
    isDowngrade: boolean;
};

export function CancelPlanOffersModal({
    isOpen,
    onClose,
    onCancel,
    onAcceptOffer,
    pricePerCredit,
    offer,
    workspaceId,
    currentPlan,
    selectedDowngradePlan,
    isDowngrade
}: CancelPlanOffersModalProps) {
    const [loadingAction, setLoadingAction] = useState<"offer" | "cancel" | null>(null);

    const creditAmount = offer.variantConfig.creditAmount ?? 0;
    const creditValue = pricePerCredit * creditAmount;

    const lastIsOpenRef = useRef(isOpen);
    useEffect(() => {
        if (isOpen === lastIsOpenRef.current) return;
        lastIsOpenRef.current = isOpen;

        eventTracker.event({
            type: `Churn Credits Offer Modal ${isOpen ? "Opened" : "Closed"}`,
            data: { workspaceId, currentPlan, selectedDowngradePlan, isDowngrade }
        });
    }, [isOpen]);

    const handleAcceptOffer = async () => {
        eventTracker.event({
            type: "Churn Credits Offer Modal Accepted",
            data: { workspaceId, currentPlan, selectedDowngradePlan, isDowngrade }
        });

        setLoadingAction("offer");
        try {
            await onAcceptOffer();
        } catch (error) {
            throw error;
        } finally {
            setLoadingAction(null);
        }
    };

    const handleCancel = async () => {
        eventTracker.event({
            type: "Churn Credits Offer Modal Rejected",
            data: { workspaceId, currentPlan, selectedDowngradePlan, isDowngrade }
        });

        setLoadingAction("cancel");
        await onCancel();
    };

    return (
        <ModalWithIcon
            isOpen={isOpen}
            onClose={onClose}
            title={offer.heading}
            icon={offer.icon}
            cancelButtonText="No Thanks, Cancel Now"
            submitButtonText={offer.ctaText}
            type="info"
            onCancel={handleCancel}
            onSubmit={handleAcceptOffer}
            submitButtonProps={{ disabled: !!loadingAction, loading: loadingAction === "offer" }}
            cancelButtonProps={{ disabled: !!loadingAction, loading: loadingAction === "cancel" }}
            closeOnCancel={false}
        >
            <div className="flex w-full flex-col gap-4 text-sm text-gray-700">
                <div className="ease-in-ou group flex w-full cursor-pointer flex-row items-center gap-6 rounded-lg border border-brand p-4 shadow-purboflow transition duration-200">
                    <div className="flex h-11 w-11 items-center justify-center rounded-md border-0 bg-tint text-brand transition duration-200">
                        <i className="far fa-coins fa-fw text-lg"></i>
                    </div>
                    <div className="flex-1 text-base font-medium text-gray-900">
                        +{creditAmount} Roboflow Credits
                    </div>
                    <div className="rounded-md bg-tint px-2 py-1 font-mono text-xs text-brand">
                        Worth ${creditValue}
                    </div>
                </div>
            </div>
        </ModalWithIcon>
    );
}
